import React, { useState } from 'react';
import { Mail, X, Plus, Trash2 } from 'lucide-react';
import Button from './ui/button';
import Input from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const SendEmailModal = ({ isOpen, onClose, onSendEmail, runData }) => {
  const [formData, setFormData] = useState({
    recipients: [''],
    format: 'summary',
    include_raw_data: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Filter out empty recipients
      const validRecipients = formData.recipients.filter(email => email.trim());
      if (validRecipients.length === 0) {
        alert('Please add at least one recipient email address');
        return;
      }

      await onSendEmail({
        ...formData,
        recipients: validRecipients,
      });
      onClose();
    } catch (error) {
      console.error('Failed to send email:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addRecipient = () => {
    setFormData(prev => ({
      ...prev,
      recipients: [...prev.recipients, '']
    }));
  };

  const removeRecipient = (index) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  const updateRecipient = (index, value) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.map((email, i) => i === index ? value : email)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Mail className="w-5 h-5" />
              <span>Send Results via Email</span>
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Run Info */}
            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Evaluation Run</p>
              <p className="font-medium">{runData?.evaluation_name}</p>
              <p className="text-sm text-muted-foreground">
                Run ID: {runData?.run_id} | Status: {runData?.status}
              </p>
              {runData?.overall_score && (
                <p className="text-sm text-muted-foreground">
                  Score: {Math.round(runData.overall_score * 100)}%
                </p>
              )}
            </div>

            {/* Recipients */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Recipients</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addRecipient}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.recipients.map((email, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => updateRecipient(index, e.target.value)}
                      className="flex-1"
                    />
                    {formData.recipients.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRecipient(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Format */}
            <div>
              <label className="block text-sm font-medium mb-2">Format</label>
              <select
                value={formData.format}
                onChange={(e) => handleChange('format', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              >
                <option value="summary">Summary</option>
                <option value="detailed">Detailed</option>
                <option value="json">JSON</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {formData.format === 'summary' && 'Basic results overview'}
                {formData.format === 'detailed' && 'Includes individual test case results'}
                {formData.format === 'json' && 'Machine-readable format'}
              </p>
            </div>

            {/* Include Raw Data */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="include_raw_data"
                checked={formData.include_raw_data}
                onChange={(e) => handleChange('include_raw_data', e.target.checked)}
                className="rounded border-input"
              />
              <label htmlFor="include_raw_data" className="text-sm">
                Include raw data (for debugging)
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SendEmailModal;
